import { useGetUserNameWithFallback } from '@pba/react-utils';
import { type EventParticipant, isRegisteredEventParticipant } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Modal, message } from 'ui';
import { useDeleteEventParticipantsMutation } from '../../queries';

export const useDeleteEventInvitee = () => {
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { isPending: isLoadingDeletingRole, mutateAsync: deleteEventInvitee } = useDeleteEventParticipantsMutation();
  const getUserNameWithFallback = useGetUserNameWithFallback();

  const confirm = useCallback(
    ({ eventId, invitee }: { eventId: string; invitee: EventParticipant }) => {
      const handleDeleteEventInvitee = async () =>
        deleteEventInvitee(
          {
            eventId,
            ...(isRegisteredEventParticipant(invitee)
              ? { participantRefs: [invitee.ref] }
              : { email: [invitee.email] }),
          },
          {
            onError() {
              message.error(
                t('Something went wrong. {{name}} couldn’t be removed from event', {
                  name: getUserNameWithFallback(invitee),
                }),
              );
            },
            onSuccess() {
              message.success(
                t('{{name}} removed from event successfully', {
                  name: getUserNameWithFallback(invitee),
                }),
              );
            },
          },
        );

      modal.confirm({
        content: 'You can invite this user again at any time.',
        icon: <Icon color="error" icon="delete-2-line" />,
        okText: 'Remove',
        onOk: handleDeleteEventInvitee,
        title: t('Are you sure you want to remove {{name}} from this event?', {
          name: getUserNameWithFallback(invitee),
        }),
        type: 'error',
      });
    },
    [deleteEventInvitee, getUserNameWithFallback, modal, t],
  );

  return useMemo(
    () => ({ confirm, contextHolder, isLoadingDeletingRole }),
    [confirm, contextHolder, isLoadingDeletingRole],
  );
};
